import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = '';

const AdminNotificationContext = createContext({
  unreadCount: 0,
  refreshCount: () => {},
  decrementCount: () => {}
});

export function AdminNotificationProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_platform_admin;
  const [unreadCount, setUnreadCount] = useState(() => {
    // Initialize from sessionStorage to preserve across navigation
    const cached = sessionStorage.getItem('adminNotifCount');
    return cached ? parseInt(cached, 10) : 0;
  });

  const fetchCount = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    
    try {
      const res = await axios.get(`${API}/api/directory/admin/notifications/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const count = res.data.unread_count || 0;
      setUnreadCount(count);
      sessionStorage.setItem('adminNotifCount', count.toString());
    } catch (error) {
      // User may not have admin access - silently ignore
      console.debug('Could not fetch admin notifications');
    }
  }, []);

  const decrementCount = useCallback(() => {
    setUnreadCount(prev => {
      const newCount = Math.max(0, prev - 1);
      sessionStorage.setItem('adminNotifCount', newCount.toString());
      return newCount;
    });
  }, []);

  const refreshCount = useCallback(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    if (!isAdmin) {
      setUnreadCount(0);
      sessionStorage.removeItem('adminNotifCount');
      return undefined;
    }
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [fetchCount, isAdmin]);

  return (
    <AdminNotificationContext.Provider value={{ unreadCount, refreshCount, decrementCount }}>
      {children}
    </AdminNotificationContext.Provider>
  );
}

export function useAdminNotifications() {
  return useContext(AdminNotificationContext);
}
